export const STATUS_DIMENSION_FILTER_KEY = 7;
export const STATUS_GRANULAR_DIMENSION = "Status";
export const EXIBE_NO_FLUXO_DIMENSION_FILTER_KEY = 8;
export const EXIBE_NO_FLUXO_DEFAULT_ENTRY = {
  mode: 1,
  ids: ["0"],
};
export const EXIBE_NO_FLUXO_INCLUDED_ENTRY = {
  mode: 1,
  ids: ["1"],
};
export const PRODUCT_SERVICE_FAMILY_FALLBACK_LABEL = "Sem família";
export const PRODUCT_SERVICE_FAMILY_NODE_PREFIX = "family:";

const EXIBE_NO_FLUXO_OPTION_LABELS = {
  "1": "Exibe no fluxo",
  "0": "Não exibe no fluxo",
};

const toTrimmedString = (value) => String(value ?? "").trim();
const normalizeProductServiceFamilyCode = (value) => {
  const normalized = toTrimmedString(value);
  return normalized && normalized !== "0" ? normalized : "";
};

export function toBackendGranularDimension(dimension) {
  return Number(dimension) === STATUS_DIMENSION_FILTER_KEY
    ? STATUS_GRANULAR_DIMENSION
    : dimension;
}

export function normalizeGranularDimensionOptionLabel(
  dimension,
  value,
  label,
) {
  const fallbackLabel = String(label ?? value ?? "").trim();
  if (Number(dimension) !== EXIBE_NO_FLUXO_DIMENSION_FILTER_KEY) {
    return fallbackLabel;
  }

  const normalizedValue = String(value ?? "").trim();
  return EXIBE_NO_FLUXO_OPTION_LABELS[normalizedValue] || fallbackLabel;
}

/**
 * @typedef {Object} ProductServiceDimensionOption
 * @property {string} value
 * @property {string} label
 * @property {string} tipo
 * @property {string | undefined} [codFamilia]
 * @property {string | undefined} [familia]
 */

export function getProductServiceFamilyLabel(
  option,
  fallbackLabel = PRODUCT_SERVICE_FAMILY_FALLBACK_LABEL,
) {
  const familyName = toTrimmedString(option?.familia ?? option?.Familia);
  const familyCode = normalizeProductServiceFamilyCode(
    option?.codFamilia ?? option?.CodFamilia,
  );

  if (familyName) return familyName;
  if (!familyCode) return fallbackLabel;

  if (familyCode) return `Família ${familyCode}`;

  return fallbackLabel;
}

function hasProductServiceFamily(option) {
  const familyName = toTrimmedString(option?.familia ?? option?.Familia);
  const familyCode = normalizeProductServiceFamilyCode(
    option?.codFamilia ?? option?.CodFamilia,
  );

  return Boolean(familyCode || familyName);
}

/**
 * @param {any} item
 * @returns {ProductServiceDimensionOption}
 */
export function normalizeProductServiceDimensionOption(item) {
  const value = toTrimmedString(
    item?.value ?? item?.Value ?? item?.id ?? item?.Id,
  );
  const label = toTrimmedString(
    item?.label ?? item?.Label ?? item?.name ?? item?.Name ?? value,
  );
  const tipo = toTrimmedString(item?.tipo ?? item?.Tipo);
  const codFamilia = normalizeProductServiceFamilyCode(
    item?.codFamilia ?? item?.CodFamilia,
  );
  const familia = toTrimmedString(item?.familia ?? item?.Familia);

  return {
    value,
    label: label || value,
    tipo,
    codFamilia: codFamilia || undefined,
    familia: familia || undefined,
  };
}

export function buildProductServiceFamilyTree(options) {
  const normalizedOptions = Array.isArray(options) ? options : [];
  const shouldGroupByFamily = normalizedOptions.some(hasProductServiceFamily);
  const groups = new Map();
  const flatLabelMap = {};
  const flatTree = [];

  normalizedOptions.forEach((option) => {
    const value = toTrimmedString(option?.value);
    const label = toTrimmedString(option?.label ?? value);
    if (!value || !label) return;

    if (!shouldGroupByFamily) {
      flatTree.push({
        key: value,
        value,
        title: label,
        isLeaf: true,
      });
      flatLabelMap[value] = label;
      return;
    }

    const familyLabel = getProductServiceFamilyLabel(option);
    const familyCode = normalizeProductServiceFamilyCode(option?.codFamilia);
    const familyKey = familyCode
      ? `${PRODUCT_SERVICE_FAMILY_NODE_PREFIX}${familyCode}`
      : `${PRODUCT_SERVICE_FAMILY_NODE_PREFIX}${familyLabel.toLowerCase()}`;

    if (!groups.has(familyKey)) {
      groups.set(familyKey, {
        key: familyKey,
        value: familyKey,
        title: familyLabel,
        selectable: false,
        children: [],
      });
    }

    groups.get(familyKey).children.push({
      key: value,
      value,
      title: label,
      isLeaf: true,
    });
    flatLabelMap[value] = label;
  });

  return {
    tree: shouldGroupByFamily
      ? Array.from(groups.values()).filter(
          (group) => Array.isArray(group.children) && group.children.length > 0,
        )
      : flatTree,
    flatLabelMap,
  };
}

export function isProductServiceFamilyNodeValue(value) {
  return toTrimmedString(value).startsWith(PRODUCT_SERVICE_FAMILY_NODE_PREFIX);
}

export function isExibeNoFluxoDimension(dimension) {
  return Number(dimension) === EXIBE_NO_FLUXO_DIMENSION_FILTER_KEY;
}

export function getExibeNoFluxoSelection(entry) {
  const ids = Array.isArray(entry?.ids || entry?.values)
    ? (entry?.ids || entry?.values).map((value) => String(value ?? "").trim())
    : [];

  if (ids.includes("1")) return "1";
  return "0";
}

export function buildStatusDimensionFilterPayload(entry) {
  const mode = Number(entry?.mode);
  if (mode !== 1 && mode !== 2) return null;

  const ids = Array.isArray(entry?.ids || entry?.values)
    ? Array.from(
        new Set(
          (entry?.ids || entry?.values)
            .map((value) => String(value ?? "").trim())
            .filter(Boolean),
        ),
      )
    : [];

  if (!ids.length) return null;

  return {
    granularDimension: STATUS_GRANULAR_DIMENSION,
    mode,
    ids,
  };
}

export function appendStatusDimensionFilter(payload, entry) {
  const base = Array.isArray(payload)
    ? payload.filter(
        (item) =>
          String(item?.granularDimension ?? "").trim().toLowerCase() !==
          STATUS_GRANULAR_DIMENSION.toLowerCase(),
      )
    : [];

  const statusFilter = buildStatusDimensionFilterPayload(entry);
  return statusFilter ? [...base, statusFilter] : base;
}
